import React, { useState, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { NewsEventCard } from "../components/ui/Card/NewsEventCard";
import { FilterPanel } from "../components/ui/FilterPanel";
import { useNewsFilter } from "../hooks/useNewsFilter";
import useAxiosPublic from "../hooks/useAxiosPublic";
import ImageHeading from "../components/ui/ImageHeading";
import LoadingPage from "../components/Layout/LoadingPage";
import { normalizeNewsEvents, getFilterOptions } from "../utils/newsEvents";
import libraryImage from "../assets/library-bg4.webp";

const NewsAndEvents = () => {
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const { ref, inView } = useInView();
  const [displayCount, setDisplayCount] = useState(6);
  const axiosPublic = useAxiosPublic();

  const {
    data: newsApiData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["news-and-events"],
    queryFn: async () => {
      const res = await axiosPublic.get("/news-and-events");
      return res?.data?.data;
    },
  });

  const items = useMemo(
    () => normalizeNewsEvents(newsApiData?.news_events),
    [newsApiData]
  );

  const { categories, audiences, itemCounts } = useMemo(
    () => getFilterOptions(items),
    [items]
  );

  // Every hook must run before the loading/error early returns below.
  const { filters, setFilters, filteredItems } = useNewsFilter(items);

  React.useEffect(() => {
    if (inView) {
      setDisplayCount((prev) => Math.min(prev + 6, filteredItems.length));
    }
  }, [inView, filteredItems.length]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="py-20 text-center text-red-500">Error loading data</div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <ImageHeading
        heading={newsApiData?.hero?.title || "News And Events"}
        imageUrl={newsApiData?.hero?.media_url || libraryImage}
      />
      <div className="container px-4 py-8 mx-auto">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
            className="p-3 border shadow-lg lg:hidden rounded-xl bg-white/80 backdrop-blur-sm border-white/20"
          >
            <Menu size={24} className="text-gray-700" />
          </motion.button>
        </motion.div>

        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Filter Panel - Mobile */}
          <AnimatePresence>
            {mobileFilterOpen && (
              <motion.div
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto shadow-2xl mt-14 sm:w-96 bg-white/80 backdrop-blur-xl lg:hidden"
              >
                <div className="p-6">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setMobileFilterOpen(false)}
                    className="absolute p-2 transition-colors bg-gray-100 top-4 right-4 rounded-xl hover:bg-gray-200"
                  >
                    <X size={24} className="text-gray-700" />
                  </motion.button>
                  <FilterPanel
                    filters={filters}
                    onFilterChange={setFilters}
                    categories={categories}
                    audiences={audiences}
                    itemCounts={itemCounts}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <div className="flex-1">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {filteredItems.slice(0, displayCount).map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <NewsEventCard item={item} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {filteredItems.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-16 text-center"
              >
                <p className="text-2xl font-medium text-gray-500">
                  No results found
                </p>
                <p className="mt-2 text-gray-400">
                  {items.length === 0
                    ? "There is nothing published yet"
                    : "Try adjusting your filters"}
                </p>
              </motion.div>
            )}

            {displayCount < filteredItems.length && (
              <div ref={ref} className="h-24" />
            )}
          </div>

          {/* Filter Panel - Desktop */}
          <div className="hidden lg:block w-96">
            <FilterPanel
              filters={filters}
              onFilterChange={setFilters}
              categories={categories}
              audiences={audiences}
              itemCounts={itemCounts}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsAndEvents;
